import { useMemo } from 'react';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useUser, useUserId } from '../../hooks/useUser';
import { useClientId } from '../../hooks/useClientId';
import { useCurrentUser } from '../../api/endpoints/useCurrentUser';
import { useEntityDetails } from '../../api/endpoints/useEntityDetails';
import { useEntityMetadata } from '../../api/endpoints/useEntityMetadata';
import { useUpdateUser } from '../../api/endpoints/useUpdateUser';
import { useClientMetadata } from '../../api/endpoints/useClientMetadata';
import {
  extractTimezoneOptions,
  extractLocaleOptions,
  getDefaultTimezone,
  getDefaultLocale,
} from './schemaUtils';
import { UPDATE_PROFILE_ACTION_REFERENCE } from './constants';

/**
 * Returns true unless both org IDs are known and differ.
 * When either is missing, defaults to allowing fetch (server decides permissions).
 */
function shouldFetchEntityDetails(
  userOrgId: string | undefined,
  clientOrgId: string | undefined
): boolean {
  if (!userOrgId || !clientOrgId) {
    return true;
  }
  return userOrgId === clientOrgId;
}

function resolvePersonnel(
  entityDetails: ReturnType<typeof useEntityDetails>['data'],
  meData: ReturnType<typeof useCurrentUser>['data']
) {
  return entityDetails?.data?.data?.entityData || meData?.data?.entityData;
}

function resolveFullName(
  personnel: ReturnType<typeof resolvePersonnel>,
  user: ReturnType<typeof useUser>['user']
) {
  return personnel?.name || user?.name || '';
}

function resolveEmailAddress(
  personnel: ReturnType<typeof resolvePersonnel>,
  user: ReturnType<typeof useUser>['user']
) {
  return personnel?.profileInformation?.emailAddress || user?.email || '';
}

function resolveConfiguredTimezone(
  personnel: ReturnType<typeof resolvePersonnel>
) {
  return personnel?.preferences?.timezone || getDefaultTimezone();
}

function resolveConfiguredLocale(
  personnel: ReturnType<typeof resolvePersonnel>
) {
  return personnel?.preferences?.localeIdentifier || getDefaultLocale();
}

/**
 * Custom hook to fetch and process user profile data
 */
export function useUserProfile() {
  const { t } = useTranslation();
  const { user } = useUser();
  const clientId = useClientId();
  const currentUserId = useUserId();
  const { data: meData } = useCurrentUser(clientId);
  const { data: clientMetadata } = useClientMetadata(clientId);

  // Wait for both to load before deciding
  const isOrgDataLoaded = Boolean(meData && clientMetadata);
  const canFetchEntityDetails =
    isOrgDataLoaded &&
    shouldFetchEntityDetails(
      meData?.data?.entityData?.organizationId?.id,
      clientMetadata?.data?.organizationId
    );

  const { data: entityDetails, refetch: refetchEntityDetails } =
    useEntityDetails(clientId, 'user', currentUserId, undefined, undefined, {
      enabled: canFetchEntityDetails,
    });
  const { data: metadataWithSchema } = useEntityMetadata(clientId, 'user', {
    includeResolvedSchema: true,
  });
  const updateUser = useUpdateUser(
    clientId,
    'user',
    currentUserId,
    t(
      'dashboard.userDetails.profileUpdateSuccess',
      'Your profile has been updated successfully.'
    )
  );

  const personnel = resolvePersonnel(entityDetails, meData);

  // Get the update action ID from entity details by reference
  const updateActionId = useMemo(() => {
    const actions = entityDetails?.data?.actions || [];
    const updateAction = actions.find(
      action => action.reference === UPDATE_PROFILE_ACTION_REFERENCE
    );
    return updateAction?.id ?? null;
  }, [entityDetails]);
  const fullName = resolveFullName(personnel, user);
  const emailAddress = resolveEmailAddress(personnel, user);
  const photo = personnel?.profileInformation?.photo;

  const configuredTimezone = resolveConfiguredTimezone(personnel);
  const configuredLocale = resolveConfiguredLocale(personnel);

  const timezoneOptions = useMemo(
    () => extractTimezoneOptions(metadataWithSchema),
    [metadataWithSchema]
  );

  const localeOptions = useMemo(
    () => extractLocaleOptions(metadataWithSchema),
    [metadataWithSchema]
  );

  return {
    fullName,
    emailAddress,
    photo,
    configuredTimezone,
    configuredLocale,
    timezoneOptions,
    localeOptions,
    isUpdating: updateUser.isPending || false,
    canUpdate: updateActionId !== null,
    clientId,
    // Exposed for useProfilePage orchestration
    personnel,
    entityDetails,
    updateUser,
    updateActionId,
    refetchEntityDetails,
  };
}
